import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PenLine, FileText, GraduationCap, Mail, Sparkles, ArrowRight, Timer, Target,
  BookOpenCheck, Brain, LineChart,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { goToIeltsAuth } from '@/lib/ieltsApi';

const TASK_CARDS = [
  {
    key: 'ac_t1', module: 'academic', task: 1, title: 'Academic · Task 1',
    icon: LineChart, meta: '20 min · 150+ words',
    desc: 'Describe a chart, graph, table, process or map. Practise the report style examiners look for.',
  },
  {
    key: 'gt_t1', module: 'general', task: 1, title: 'General · Task 1',
    icon: Mail, meta: '20 min · 150+ words',
    desc: 'Write a formal, semi-formal or informal letter with the correct tone, structure, and bullets.',
  },
  {
    key: 'ac_t2', module: 'academic', task: 2, title: 'Academic · Task 2',
    icon: BookOpenCheck, meta: '40 min · 250+ words',
    desc: 'Opinion, discussion, problem/solution, advantages/disadvantages, double-question essays.',
  },
  {
    key: 'gt_t2', module: 'general', task: 2, title: 'General · Task 2',
    icon: GraduationCap, meta: '40 min · 250+ words',
    desc: 'Same essay format as Academic Task 2 — slightly more familiar topics.',
  },
];

const FEATURES = [
  { icon: Timer, title: 'Real exam clock', desc: 'Two timers (20 + 40 min) running exactly like the real CBT exam.' },
  { icon: Brain, title: 'AI band feedback', desc: 'Claude rates your essay on the 4 IELTS descriptors with sentence-level edits.' },
  { icon: Target, title: 'Live word counter', desc: 'Word count, autosave and a structured workflow that mirrors test day.' },
];

const IeltsWritingLandingPage = () => {
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const start = (module, task) => {
    goToIeltsAuth(`/ielts/writing/practice?module=${module}&task=${task}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="ielts-writing-landing">
      <Seo title="IELTS Writing · Winhouse Tutors" canonical="/ielts/writing"
        description="Practise IELTS Writing Task 1 and Task 2 with real-exam timers, live word counts and AI band feedback from Claude." />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <FloatingActionButtons />

      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden bg-black text-white">
          <div className="absolute inset-0 opacity-90" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(220,38,38,0.35) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 85% 20%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%), linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
          }} />
          <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-20 sm:pt-36 sm:pb-24">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" /> IELTS Writing AI
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                Write like a
                <span className="block text-[#D3D86F]">Band 8 candidate.</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/75 max-w-2xl mt-6 leading-relaxed">
                Real-exam Task 1 + Task 2 practice with split-screen UI, live word counts, autosave
                and instant AI band feedback against the four IELTS descriptors.
              </p>
              <div className="flex flex-wrap gap-3 mt-9">
                <Button onClick={() => start('academic', 1)}
                  className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 text-base rounded-full"
                  data-testid="ielts-writing-cta-start">
                  Start Writing Practice <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button onClick={() => goToIeltsAuth('/ielts/writing/mock')}
                  variant="outline"
                  className="bg-white/5 backdrop-blur border-white/25 text-white hover:bg-white/15 hover:text-white px-7 h-12 text-base rounded-full"
                  data-testid="ielts-writing-cta-mock">
                  Take Full Writing Mock (60 min)
                </Button>
                <Button onClick={() => navigate('/ielts/writing/dashboard')}
                  variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 px-5 h-12 text-base rounded-full"
                  data-testid="ielts-writing-cta-dashboard">
                  My Writing Dashboard →
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* TASK CARDS */}
        <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Four practice modes</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Pick a task — start in seconds.</h2>
            <p className="text-gray-600 mt-3 text-base">Every prompt is original and modelled on the real CBT exam. AI band feedback after every submission.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {TASK_CARDS.map(({ key, module, task, icon: Icon, title, meta, desc }) => (
              <button key={key} onClick={() => start(module, task)}
                className="text-left bg-white border border-gray-200 rounded-2xl p-6 hover:border-red-600 hover:-translate-y-0.5 transition-all group"
                data-testid={`ielts-writing-card-${key}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-6 w-6 text-red-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1.5">{meta}</div>
                    <h3 className="font-bold text-lg mb-2">{title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">{desc}</p>
                    <span className="inline-flex items-center text-sm font-semibold text-black group-hover:text-red-700">
                      Begin practice <ArrowRight className="h-4 w-4 ml-1" />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* FEATURES */}
        <section className="bg-[#FAF9F6] py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="max-w-2xl mb-10">
              <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Why Winhouse Writing</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">A real CBT writing experience.</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-black transition-all">
                  <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#D3D86F]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              80 original prompts. <span className="text-[#D3D86F]">AI feedback after every essay.</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8 text-base sm:text-lg">
              The fastest way from "I can't write essays" to "I know exactly what Band 7 looks like."
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button onClick={() => start('academic', 2)}
                className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 rounded-full">
                Practise a Task 2 Essay <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Link to="/ielts">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white px-7 h-12 rounded-full">
                  Back to IELTS Hub
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
export default IeltsWritingLandingPage;
